import React, { FC, useCallback, useEffect, useReducer } from 'react';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { NavigateFunction } from 'react-router';
import { cardService } from '../../../../../channels/secure-web/services';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useCardHeaders } from '../../../../transaction/src/contexts/Parameters/hooks';
import { Card } from '../../domain/Card/entities/Card';
import { useCards, useIsCorporateCardProvider, useRefetchCardsListMutation } from '../Card';
import { useOnSetSelectedCard, useSelectedCard, useSelectedCardAction } from '../CardDetails';
import { useCardDetails, useRefetchCardDetailsMutation } from '../CardDetails';
import { defaultCardActionProviderState } from './defaultState';
import { CardActionsReducer } from './CardActionsReducer';
import { CardActionsContext, CardActionsDispatchContext } from './CardActionsContext';
import { CardAction, CardActionProviderProps } from './types';

const CardActionsProvider: FC<CardActionProviderProps> = ({ children }) => {
  const customerId = useSelectedCustomerId();
  const cardHeaders = useCardHeaders();
  // Get initial state values
  const [state, dispatch] = useReducer(CardActionsReducer, defaultCardActionProviderState);
  const cardListData = useCards();
  const selectedCard = useSelectedCard();
  const cardDetailsData = useCardDetails();
  const onSetSelectedCard = useOnSetSelectedCard();
  const selectedCardAction = useSelectedCardAction();
  const refetchCardDetails = useRefetchCardDetailsMutation();
  const refetchCardsDataMutation = useRefetchCardsListMutation();
  const isCorporate = useIsCorporateCardProvider();

  // Define api call conditions
  const shouldFreezeCard =
    !isEmpty(cardListData) &&
    !isEmpty(selectedCard) &&
    (selectedCardAction?.action === CardAction.freezeCard || selectedCardAction?.action === CardAction.unfreezeCard);

  const { data: freezeCardData, error: freezeCardDataError } = useInvokeSWRService(
    cardService.FreezeCardAction,
    {
      headers: cardHeaders,
      customerId,
      action: selectedCardAction?.action,
      paymentCardIdentifications: selectedCard?.paymentCardIdentifications ?? [],
      meta: {
        timestamp: selectedCardAction?.timestamp,
      },
      successCallback: () => {
        refetchCardsDataMutation && refetchCardsDataMutation();
        refetchCardDetails && refetchCardDetails();
      },
    },
    shouldFreezeCard,
    'cardService.FreezeCardAction',
  );

  useEffect(() => {
    if (
      selectedCardAction?.action === CardAction.freezeCard ||
      selectedCardAction?.action === CardAction.unfreezeCard
    ) {
      if (cardDetailsData) {
        onSetSelectedCard(selectedCard, null);
      }
    }
  }, [selectedCard, cardDetailsData, cardListData, selectedCardAction?.action, onSetSelectedCard]);

  const onViewCardDetails = useCallback(
    (card: Nullable<Card>, navigate?: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.viewDetails);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'card-details', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, onSetSelectedCard, isCorporate],
  );

  const onTopUpCard = useCallback(
    (card: Nullable<Card>, navigate?: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.topUpCard);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'top-up-card', customerId, cardId: card?.cardId, isCorporate }), {
          replace: true,
        });
      }
    },
    [customerId, onSetSelectedCard, isCorporate],
  );

  const onFreezeCard = useCallback(
    (card: Nullable<Card>) => {
      onSetSelectedCard(card, CardAction.freezeCard);
    },
    [onSetSelectedCard],
  );
  const onUnfreezeCard = useCallback(
    (card: Nullable<Card>) => {
      onSetSelectedCard(card, CardAction.unfreezeCard);
    },
    [onSetSelectedCard],
  );
  const onChangePIN = useCallback((card: Nullable<Card>) => {
    console.log('onChangePIN');
  }, []);
  const onRenewCard = useCallback(
    (card: Nullable<Card>, navigate: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.renewCard);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'replace-card', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, onSetSelectedCard, isCorporate],
  );

  const onPermanentBlockCard = useCallback(
    (card: Nullable<Card>, navigate: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.permanentBlockCard);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'permanent-block-card', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, onSetSelectedCard, isCorporate],
  );

  const onReportCardAsDestroyed = useCallback((card: Nullable<Card>) => {
    console.log('onReportCardAsDestroyed');
  }, []);
  const onReportCardAsStolen = useCallback((card: Nullable<Card>) => {
    console.log('onReportCardAsStolen');
  }, []);
  const onResetPINRetry = useCallback((card: Nullable<Card>) => {
    console.log('onResetPINRetry');
  }, []);
  const onLimits = useCallback((card: Nullable<Card>, navigate: NavigateFunction) => {
    console.log('onLimit');
  }, []);
  const onTerminateCard = useCallback(
    (card: Nullable<Card>, navigate?: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.terminateCard);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'terminate-card', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, onSetSelectedCard, isCorporate],
  );
  const onActivateCard = useCallback(
    (card: Nullable<Card>, navigate?: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.activateCard);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'activate-card', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, isCorporate, onSetSelectedCard],
  );

  const onResetPinTriesExceeded = useCallback(
    (card: Nullable<Card>, navigate?: NavigateFunction) => {
      onSetSelectedCard(card, CardAction.resetPinAttempts);
      if (navigate) {
        navigate(getRoutePath({ routeKey: 'reset-pin-attempts', customerId, cardId: card?.cardId, isCorporate }));
      }
    },
    [customerId, isCorporate, onSetSelectedCard],
  );

  return (
    <CardActionsContext.Provider
      value={{
        ...state,
        freezeCardData,
        errorState: {
          freezeCardDataError,
        },
        onViewCardDetails,
        onTopUpCard,
        onFreezeCard,
        onUnfreezeCard,
        onChangePIN,
        onRenewCard,
        onReportCardAsDestroyed,
        onReportCardAsStolen,
        onResetPINRetry,
        onLimits,
        onTerminateCard,
        onActivateCard,
        onPermanentBlockCard,
        onResetPinTriesExceeded,
      }}>
      <CardActionsDispatchContext.Provider value={dispatch}>{children}</CardActionsDispatchContext.Provider>
    </CardActionsContext.Provider>
  );
};
export { CardActionsProvider };
